import { useState, useMemo } from 'react';
import {
  Product,
  SupplierTariff,
  Parameters,
  INITIAL_PRODUCTS,
  INITIAL_P1_TARIFFS,
  INITIAL_P2_TARIFFS,
  INITIAL_PARAMETERS
} from '@/lib/index';

interface DashboardState {
  products: Product[];
  p1Tariffs: SupplierTariff[];
  p2Tariffs: SupplierTariff[];
  parameters: Parameters;
}

interface ComputedValues {
  grossP1: number;
  grossP2: number;
  billingBenefit: number;
  netP1: number;
  netP2: number;
  savingsEur: number;
  savingsPct: number;
  p1Contribution: number;
  p2Contribution: number;
  furnitureAdjustment: number;
  p1RappelAmount: number;
  p2RappelAmount: number;
  winnerNetCost: number;
  vatAmount: number;
  totalWithVat: number;
  loserNetCost: number;
  loserTotalWithVat: number;
  winnerName: string;
}

export function useDashboard() {
  const [state, setState] = useState<DashboardState>({
    products: INITIAL_PRODUCTS,
    p1Tariffs: INITIAL_P1_TARIFFS,
    p2Tariffs: INITIAL_P2_TARIFFS,
    parameters: INITIAL_PARAMETERS
  });

  const computed = useMemo<ComputedValues>(() => {
    const grossP1 = state.products.reduce((sum, p) => {
      return sum + p.units * p.priceP1;
    }, 0);

    const grossP2 = state.products.reduce((sum, p) => {
      return sum + p.units * p.priceP2;
    }, 0);

    const billingBenefit = grossP2 - grossP1;

    const p1Contribution = state.parameters.p1FixedContribution;
    const p2Contribution =
      state.parameters.p2EurPerLitre * state.parameters.p2LitresSold;

    const furnitureAdjustment = state.parameters.includeFurniture
      ? state.parameters.furnitureCost
      : 0;

    const p1RappelAmount = state.parameters.p1RappelAmount;
    const p2RappelAmount = state.parameters.p2RappelAmount;

    const netP1 = grossP1 - p1Contribution - billingBenefit - p1RappelAmount;
    const netP2 = grossP2 - p2Contribution + furnitureAdjustment + billingBenefit - p2RappelAmount;

    const savingsEur = netP2 - netP1;
    const savingsPct = netP2 !== 0 ? (savingsEur / netP2) * 100 : 0;

    const winnerNetCost = Math.min(netP1, netP2);
    const vatAmount = winnerNetCost * (state.parameters.vatRate / 100);
    const totalWithVat = winnerNetCost + vatAmount;

    const loserNetCost = Math.max(netP1, netP2);
    const loserTotalWithVat = loserNetCost + (loserNetCost * state.parameters.vatRate / 100);

    const winnerName = netP1 < netP2 ? 'Proveedor 1' : 'Proveedor 2';

    return {
      grossP1,
      grossP2,
      billingBenefit,
      netP1,
      netP2,
      savingsEur,
      savingsPct,
      p1Contribution,
      p2Contribution,
      furnitureAdjustment,
      p1RappelAmount,
      p2RappelAmount,
      winnerNetCost,
      vatAmount,
      totalWithVat,
      loserNetCost,
      loserTotalWithVat,
      winnerName
    };
  }, [state]);

  const updateProduct = (
    id: string,
    field: keyof Product,
    value: string | number
  ) => {
    setState((prev) => ({
      ...prev,
      products: prev.products.map((p) =>
        p.id === id ? { ...p, [field]: value } : p
      )
    }));
  };

  const deleteProduct = (id: string) => {
    setState((prev) => ({
      ...prev,
      products: prev.products.filter((p) => p.id !== id)
    }));
  };

  const addProduct = () => {
    const newProduct: Product = {
      id: `product-${Date.now()}`,
      name: 'Nuevo Producto',
      units: 0,
      priceP1: 0,
      priceP2: 0
    };
    setState((prev) => ({
      ...prev,
      products: [...prev.products, newProduct]
    }));
  };

  const updateTariff = (
    supplier: 'p1' | 'p2',
    id: string,
    field: keyof SupplierTariff,
    value: number
  ) => {
    const tariffKey = supplier === 'p1' ? 'p1Tariffs' : 'p2Tariffs';
    setState((prev) => ({
      ...prev,
      [tariffKey]: prev[tariffKey].map((t) =>
        t.id === id ? { ...t, [field]: value } : t
      )
    }));
  };

  const updateParameter = (
    field: keyof Parameters,
    value: number | boolean
  ) => {
    setState((prev) => ({
      ...prev,
      parameters: { ...prev.parameters, [field]: value }
    }));
  };

  return {
    products: state.products,
    p1Tariffs: state.p1Tariffs,
    p2Tariffs: state.p2Tariffs,
    parameters: state.parameters,
    ...computed,
    updateProduct,
    deleteProduct,
    addProduct,
    updateTariff,
    updateParameter
  };
}